import { LOGGING } from '@/services/api/userlog'
import { client } from '@/services/apolloclient'
import store from '@/state/store'
import jwt_decode from 'jwt-decode'
import moment from 'moment'
import i18n from '@/i18n'
import DOMPurify from 'dompurify'
import { GetAddressData } from '@/services/api/map'
import forge from 'node-forge'
import { getGeolocation } from '@/services/api/geolocation'
import { DEVICE_CODE_LIST } from '@/helpers/device-code'
import Vue from 'vue'
import {
  TRACKVISION_CHANNEL_DEFAULT_KEY,
  TRACKVISION_CHANNEL_MAPPING,
} from '@/helpers/trackvision-channel-mapping'
import muxjs from 'mux.js'
import msgpack from 'msgpack-lite'

moment.locale('id')
const accessRight = ['ADMIN', 'SUPER_ADMIN']

export const Helper = {
  methods: {
    /**
     * Current Date Time
     * @param {date} date
     * @returns
     */
    now() {
      return moment().toISOString()
    },
    /**
     * Current Date Time
     * @param {date} date
     * @returns
     */
    today() {
      const now = moment().format('YYYY-MM-DD')
      return [moment(`${now} 00:00:00`).toISOString(), moment(`${now} 23:59:59`).toISOString()]
    },
    /**
     * convert date to ISO String
     * @param {date} date
     * @returns
     */
    momentToISOString(date) {
      return moment(date).toISOString()
    },

    /**
     * convert date to MMMM YYYY (Agustus 2022)
     * @param {date} date
     * @returns
     */
    momentYM(date) {
      return moment(date).format('MMMM YYYY')
    },

    /**
     * Convert a date to a formatted string in the user's preferred language.
     * The format is "MMMM YYYY" (e.g. "Agustus 2022").
     * The user's preferred language is retrieved from localStorage, falling back to 'id' if not set.
     * @param {date} date - The date to be formatted.
     * @returns {string} The formatted date string.
     */
    momentMY(date) {
      const getLanguage = localStorage.getItem('language') ? localStorage.getItem('language') : 'id'
      moment.locale(getLanguage)
      return moment(date, 'YYYY-MM').format('MMMM YYYY')
    },

    /**
     * convert date to YYYY-MM-DD (2022-12-31)
     * @param {date} date
     * @returns
     */
    momentYmD(date) {
      return moment(date).format('YYYY-MM-DD')
    },

    /**
     * convert date to HH:mm:ss (12:55:01)
     * @param {date} date
     * @returns
     */
    momentHms(date) {
      return moment(date).format('HH:mm:ss')
    },

    /**
     * convert date to YYYY-MM-DD (2022-12-31 12:55)
     * @param {date} date
     * @returns
     */
    momentYmDHm(date) {
      return moment(date).format('YYYY-MM-DD HH:mm')
    },

    /**
     * convert date to YYYY-MM-DD (2022-12-31 12:55)
     * @param {date} date
     * @returns
     */
    momentDmMmYH(date, useTimezoneLabel) {
      let dateTime = moment(date).format('DD-MM-YYYY HH:mm')
      dateTime += date && useTimezoneLabel ? ` ${this.getLabelTimezone()}` : ''
      return dateTime
    },

    /**
     * convert date to YYYY-MM-DD (2022-12-31 12:55:22)
     * @param {date} date
     * @returns
     */
    momentYmDHms(date) {
      return moment(date).format('YYYY-MM-DD HH:mm:ss')
    },

    /**
     * convert date to DD MMMM YYYY HH:mm (31 Agustus 2022 12:55)
     * @param {date} date
     * @returns
     */
    momentDMYH(date) {
      return moment(date).format('DD MMMM YYYY HH:mm')
    },

    /**
     * convert date to DD MMMM YYYY HH:mm:ss (31 Agustus 2022 12:55:20)
     * @param {date} date
     * @returns
     */
    momentDMYHS(date, useTimezoneLabel) {
      let dateTime = moment(date).format('DD MMMM YYYY HH:mm:ss')
      dateTime += date && useTimezoneLabel ? ` ${this.getLabelTimezone()}` : ''
      return dateTime
    },

    /**
     * convert date to DD MMMM YYYY HH:mm (31 Agu 2022 12:55)
     * @param {date} date
     * @returns
     */
    momentDMY_HI(date) {
      return moment(date).format('DD MMM YYYY HH:mm')
    },

    /**
     * convert date to DD MMMM YYYY HH:mm (18 Agt 17:39)
     * @param {date} date
     * @returns
     */
    momentDMHm(date) {
      return moment(date).format('D MMM HH:mm')
    },

    /**
     * convert date to DD MMMM YYYY HH:mm:ss (18 Agt 17:39:00)
     * @param {date} date
     * @returns
     */
    momentDMHms(date) {
      return moment(date).format('DD MMM YYYY HH:mm:ss')
    },

    /**
     * convert date to DD MMM YYYY short month
     * @param {date} date
     * @returns
     */
    momentDateShortMonth(date) {
      const getLanguage = localStorage.getItem('language') ? localStorage.getItem('language') : 'id'
      moment.locale(getLanguage)
      return !date ? '-' : moment(date).format('DD MMM YYYY')
    },

    /**
     * convert datetime to DD MMM YYYY HH:mm:ss short month
     * @param {date} date
     * @returns
     */
    momentDatetimeShortMonth(date, useTimezoneLabel) {
      const getLanguage = localStorage.getItem('language') ? localStorage.getItem('language') : 'id'
      moment.locale(getLanguage)
      return !date
        ? '-'
        : useTimezoneLabel
        ? `${moment(date).format('DD MMM YYYY HH:mm:ss')} ${this.getLabelTimezone()}`
        : moment(date).format('DD MMM YYYY HH:mm:ss')
    },

    /**
     * convert datetime to DD MMM YYYY HHmmss short month
     * @param {date} date
     * @returns
     */
    momentDatetimeShortMonthPlain(date) {
      const getLanguage = localStorage.getItem('language') ? localStorage.getItem('language') : 'id'
      moment.locale(getLanguage)
      return !date
        ? '-'
        : moment(date)
            .format('DDMMMYYYY HHmmss')
            .replace(/([A-Za-z]{3})/, (match) => match.toUpperCase())
    },

    /**
     * convert date to DD MMM YYYY short month
     * @param {date} date
     * @returns
     */
    momentDateLongMonth(date) {
      const getLanguage = localStorage.getItem('language') ? localStorage.getItem('language') : 'id'
      moment.locale(getLanguage)
      return moment(date).format('DD MMMM YYYY')
    },

    /**
     * convert date to DD MMM YYYY short month
     * @param {date} date
     * @returns
     */
    momentTime(date) {
      return moment(date).format('HH:mm')
    },

    /**
     * convert date to HH:mm:ss short month
     * @param {date} date
     * @returns
     */
    momentTimeSeconds(date, useTimezoneLabel) {
      let time = !date ? '-' : moment(date).format('HH:mm:ss')
      time += date && useTimezoneLabel ? ` ${this.getLabelTimezone()}` : ''
      return time
    },

    momentToSetHours(date) {
      const hours = moment(date).format('HH')
      const min = moment(date).format('mm')
      const sec = moment(date).format('ss')

      return [hours, min, sec]
    },

    momentM(date) {
      return Number(moment(date).format('M'))
    },

    momentY(date) {
      return Number(moment(date).format('YYYY'))
    },

    momentMMM(monthIndex) {
      return moment(Number(monthIndex), 'M').format('MMMM')
    },

    momentYYYYMMDDFromIndex(monthIndex, yearIndex) {
      return moment(
        new Date(`${yearIndex}-${monthIndex}-01 00:00:00`),
        'YYYY-MM-DD HH:mm:ss'
      ).format('YYYY-MM-DD HH:mm:ss')
    },
    momentAdd(date, num, unit) {
      return moment(date).add(num, unit)
    },
    momentFirstAndEnd(date) {
      const currentDate = moment(date) // Get the current date
      const lastDayOfMonth = currentDate.clone().endOf('month') // Get the last day of the current month
      return [lastDayOfMonth._i.toISOString(), lastDayOfMonth._d.toISOString()]
    },
    /**
     * format date to ISO String with seconds and milliseconds set to 0
     * @param {string|date} val
     * @returns {string} ISO String
     */
    formatToMinute(val) {
      if (!val) return null
      const m = moment(val)
      return m.isValid() ? m.startOf('minute').format('YYYY-MM-DDTHH:mm:ss.SSSZ') : val
    },
    /**
     * Find range between 2 date
     * @param {date} startDate
     * @param {date} endDate
     * @returns
     */
    getDatesInRange(startDate, endDate) {
      startDate = new Date(startDate)
      endDate = new Date(endDate)
      const date = new Date(startDate.getTime())

      const dates = []

      while (date <= endDate) {
        dates.push(new Date(date))
        date.setDate(date.getDate() + 1)
      }

      return dates
    },

    /**
     * Get the total number in a certain month
     * @param {date} date
     * @returns
     */
    getDaysInMonth(date) {
      const year = new Date(date).getYear()
      const month = new Date(date).getMonth() + 1

      return new Date(year, month, 0).getDate()
    },

    /**
     * Convert number to time
     * @param {int} num
     * @returns
     */
    minuteToTime(num) {
      const d = Math.floor(num / 1440) // 60*24
      const h = Math.floor((num - d * 1440) / 60)
      const m = Math.round(num % 60)
      if (d != 0) {
        return d + ' hari +'
      } else if (h != 0) {
        return h + ' jam +'
      } else {
        return m + ' menit'
      }
    },

    /**
     * Convert minute to hour
     * @param {int} num
     * @returns
     */
    minuteToHour(num) {
      const h = num > 0 ? Math.floor(num / 60) : Math.ceil(num / 60)
      const m = Math.abs(Math.round(num % 60))
      if (h != 0) {
        return `${h} ${this.$t('global.time.hour')} ${
          m > 0 ? m + ' ' + this.$t('global.time.minutes') : ''
        }`
      } else if (h == 0 && m > 0) {
        return `${m} ${this.$t('global.time.minutes')}`
      } else {
        return '-'
      }
    },

    /**
     * Convert seconds to hours
     * @param {int} num
     * @returns
     */
    secondsToHours(num) {
      const h = num > 0 ? Math.floor(num / 3600) : Math.ceil(num / 3600)
      const m = Math.floor(Math.abs(Math.round(num % 3600) / 60))
      const s = Math.abs(Math.round(num % 3600) % 60)

      if (h != 0) {
        return `${this.thousandFormat(h)} ${this.$t('global.time.hour')} ${
          m > 0 ? m + ' ' + this.$t('global.time.minutes') : ''
        } ${s > 0 ? s + ' ' + this.$t('global.time.seconds') : ''}`
      } else {
        if (m != 0) {
          return `${m > 0 ? m + ' ' + this.$t('global.time.minutes') : ''} ${
            s > 0 ? s + ' ' + this.$t('global.time.seconds') : ''
          }`
        } else {
          if (s != 0) {
            return `${s > 0 ? s + ' ' + this.$t('global.time.seconds') : ''}`
          } else {
            return '-'
          }
        }
      }
    },

    /**
     * Convert seconds to hours
     * @param {int} num
     * @returns
     */
    msToHours(num, format) {
      let formattedNum = num
      if (format == 'iso8601') {
        formattedNum = moment.duration(num)
      }

      const h =
        formattedNum > 0 ? Math.floor(formattedNum / 3600000) : Math.ceil(formattedNum / 3600000)
      const m = Math.floor(Math.abs(Math.round(formattedNum % 3600000) / 60000))
      const s = Math.abs(Math.round(formattedNum % 60000) / 1000)

      if (h != 0) {
        return `${this.thousandFormat(h)} ${this.$t('global.time.hour')} ${
          m > 0 ? m + ' ' + this.$t('global.time.minutes') : ''
        } ${s > 0 ? s + ' ' + this.$t('global.time.seconds') : ''}`
      } else {
        if (m != 0) {
          return `${m > 0 ? m + ' ' + this.$t('global.time.minutes') : ''} ${
            s > 0 ? s + ' ' + this.$t('global.time.seconds') : ''
          }`
        } else {
          if (s != 0) {
            return `${s > 0 ? s + ' ' + this.$t('global.time.seconds') : ''}`
          } else {
            return '-'
          }
        }
      }
    },

    /**
     * convert date to HH:mm {timezone} DD/MM/YYYY
     * @param {date} date
     * @returns
     */
    timeWithLabelAndShortDate(date) {
      if (!date) return
      let time = moment(date).format('HH:mm')
      let shortDate = moment(date).format('DD/MM/YYYY')
      return `${time} ${this.getLabelTimezone()} ${shortDate}`
    },

    /**
     * Gets a relative time sentence from a given date.
     * @param date - A Date object or ISO string
     * @returns Relative time string or false if date is invalid
     * @example
     * // If current time is 2024-01-20T15:30:00
     * checkTime("2024-01-20T14:30:00") // returns "1 hour ago"
     * checkTime("2024-01-19T15:30:00") // returns "Yesterday"
     * checkTime("2024-01-18T15:30:00") // returns "18 January 2024"
     */
    checkTimePassed(time) {
      const now = moment()
      const timePassedMoment = moment(time)
      const diffHours = now.diff(timePassedMoment, 'hours')

      if (diffHours < 24) {
        return moment(time).fromNow()
      }

      const yesterday = moment().subtract(1, 'day').startOf('day')
      const timeDate = moment(time).startOf('day')
      if (timeDate.isSame(yesterday)) {
        return this.$t('global.time.yesterday')
      }

      return this.momentDateLongMonth(time)
    },

    getLiveTimePassed(time, currentTime) {
      if (!time) return 'Loading...'

      const diffSeconds = currentTime.diff(moment(time), 'seconds')

      if (diffSeconds < 0) return '00:00:00'

      const hours = Math.floor(diffSeconds / 3600)
      const minutes = Math.floor((diffSeconds % 3600) / 60)
      const seconds = diffSeconds % 60

      const pad = (n) => n.toString().padStart(2, '0')

      if (hours < 24) {
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      } else if (hours >= 24 && hours <= 48) {
        return this.$t('global.time.yesterday')
      } else {
        return this.momentDateLongMonth(time)
      }
    },

    /**
     * Convert time to object
     * @param {int} num
     * @returns
     */
    convertTimeToObject(time) {
      const result = {
        day: 0,
        hour: 0,
        minute: 0,
        second: 0,
      }
      // return empty if time is null or empty
      if (time == null || time == '') {
        return ''
      }
      time = Math.floor(time)

      // If the time is less than 1 minute
      if (time < 60) {
        result.second = time
        // If the time is more than or equals to 1 minute and less than 1 hour
      } else if (time >= 60 && time < 3600) {
        let seconds = time % 60
        let minutes = Math.floor(time / 60)
        result.minute = minutes
        result.second = seconds
        // If the time is more than or equals to 1 hour and less than 1 day
      } else if (time >= 3600 && time < 86400) {
        let seconds = time % 60

        let minutes = Math.floor(time / 60)
        let modminutes = minutes % 60

        let hours = Math.floor(minutes / 60)

        result.second = seconds
        result.minute = modminutes
        result.hour = hours
        // If the time is more than or equals to 1 day
      } else {
        let seconds = time % 60

        let minutes = Math.floor(time / 60)
        let modminutes = minutes % 60

        let hours = Math.floor(minutes / 60)
        let modhours = hours % 24

        let days = Math.floor(hours / 24)

        result.day = days
        result.hour = modhours
        result.minute = modminutes
        result.second = seconds
      }
      return result
    },

    localizeMonth(stringMonth) {
      if (stringMonth == 'January' || stringMonth == 'Januari') {
        return this.$t('global.longMonths.jan')
      } else if (stringMonth == 'February' || stringMonth == 'Februari') {
        return this.$t('global.longMonths.feb')
      } else if (stringMonth == 'March' || stringMonth == 'Maret') {
        return this.$t('global.longMonths.mar')
      } else if (stringMonth == 'April') {
        return this.$t('global.longMonths.apr')
      } else if (stringMonth == 'May' || stringMonth == 'Mei') {
        return this.$t('global.longMonths.may')
      } else if (stringMonth == 'June' || stringMonth == 'Juni') {
        return this.$t('global.longMonths.jun')
      } else if (stringMonth == 'July' || stringMonth == 'Juli') {
        return this.$t('global.longMonths.jul')
      } else if (stringMonth == 'August' || stringMonth == 'Agustus') {
        return this.$t('global.longMonths.aug')
      } else if (stringMonth == 'September') {
        return this.$t('global.longMonths.sep')
      } else if (stringMonth == 'October' || stringMonth == 'Oktober') {
        return this.$t('global.longMonths.oct')
      } else if (stringMonth == 'November') {
        return this.$t('global.longMonths.nov')
      } else {
        return this.$t('global.longMonths.dec')
      }
    },

    /**
     * Get Grade from score
     * @param {int} score
     * @returns
     */
    getGrade(score) {
      if (score > 80) {
        return 'A'
      } else if (score <= 80 && score >= 61) {
        return 'B'
      } else if (score <= 60 && score >= 41) {
        return 'C'
      } else if (score <= 40 && score >= 21) {
        return 'D'
      } else {
        return 'E'
      }
    },

    /**
     * Get Company ID from Token
     * @param {int} score
     * @returns
     */
    getCompanyId() {
      return store.state.identity.whoami?.companyId
    },

    /**
     * Get Company ID from Token
     * @param {int} score
     * @returns
     */
    getCompanyName() {
      return store.state.identity.whoami?.companyName
    },

    /**
     * Get Logged in user name
     * @param {int} score
     * @returns
     */
    getUserName() {
      return store.state.identity.whoami?.name
    },

    /**
     * Get Logged in user id
     * @param {int} score
     * @returns
     */
    getUserId() {
      return store.state.identity.whoami?.userId
    },

    /**
     * Get Logged in email
     * @param {int} score
     * @returns
     */
    getUserEmail() {
      return store.state.identity.whoami?.email
    },

    /**
     * Get Logged in is bypass
     * @returns
     */
    getIsBypass() {
      return store.state.identity.whoami?.isBypass
    },

    /**
     *
     * @returns
     *
     */
    getAccessRight() {
      if (accessRight.includes(store.state.identity.whoami?.accessType)) {
        return true
      } else {
        return false
      }
    },

    /**
     * Get Logged in user access right QA On Demand
     * @returns
     */
    getAccessRightQaOnDemand() {
      return store.state.identity.whoami?.accessRight === 'QA On Demand'
    },

    /**
     * Get logged in user image
     * @param {int} score
     * @returns
     */
    getUserPhoto() {
      if (!store.state.identity.whoami == null) return 'https://api.dicebear.com/7.x/initials/svg'
      return `https://api.dicebear.com/7.x/initials/svg?seed=${store.state.identity.whoami?.name}`
    },

    /**
     * Get Company Name from Token
     * @param {string} token
     * @returns
     */
    getCompanyNameFromToken(token) {
      if (!token) {
        return false
      }

      const decodedToken = jwt_decode(token)

      return decodedToken.companyName
    },

    /**
     * Get Logged in user name from token
     * @param {string} token
     * @returns
     */
    getUserNameFromToken(token) {
      if (!token) {
        return false
      }

      const decodedToken = jwt_decode(token)

      if (!decodedToken) {
        return 'Guest'
      }

      return decodedToken.name
    },

    /**
     * Get Logged in user id from token
     * @param {string} token
     * @returns
     */
    getUserIdFromToken(token) {
      if (!token) {
        return false
      }

      const decodedToken = jwt_decode(token)

      if (!decodedToken) {
        return null
      }

      return decodedToken.userId
    },

    /**
     * Get Logged in Access Rights from token
     * @param {string} token
     * @returns
     */
    getAccessFromToken(token) {
      if (!token) {
        return false
      }

      const decodedToken = jwt_decode(token)

      if (!decodedToken) {
        return null
      }

      return decodedToken.accessRight
    },

    /**
     * Get Logged in Access Rights from token
     * @param {string} token
     * @returns
     */
    getAccess() {
      return store.state.identity.whoami?.accessType
    },

    isSuperUser() {
      return store.state.identity.whoami?.groups.some(
        (g) => g.name === 'Super Administrator' || g.name === 'Super User Operational'
      )
    },

    getUserPosition() {
      return store.state.identity.whoami?.position
    },

    /**
     * Check user if has access to edit / delete
     * @param {int} score
     * @returns
     */
    hasAccessEditAndDelete(dataCompanyId) {
      const companyId = this.getCompanyId()
      if (!companyId) {
        return false
      }

      if ((companyId == 1 && dataCompanyId == 1) || companyId == dataCompanyId) {
        return true
      }

      return false
    },

    /**
     * Check user if cant delete account
     * @param {int} dataUserId
     */
    cannotDelete(dataUserId) {
      const userId = this.getUserId()
      if (!userId) {
        return false
      }
      if (userId == dataUserId) {
        return true
      }

      return false
    },

    /**
     * Minimize float length to two digit
     * @param {float} score
     * @returns
     */
    minimizeFloat(data) {
      return parseFloat(data).toFixed(2)
    },

    thousandFormat(data) {
      if (data == null || isNaN(data)) return null
      return Number(data).toLocaleString('id-ID', { maximumFractionDigits: 2 })
    },
    reverseThousandFormat(data) {
      return parseFloat(data.replace(/\./g, '').replace(',', '.')) || 0
    },
    localeThousandFormat(data) {
      // Convert the number to a string and replace the dot with a comma
      let formattedNumber = data.toString().replace('.', ',')

      // Add dots as thousands separators to the whole part
      formattedNumber = formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

      return formattedNumber
    },

    /**
     * Get Days from Hours
     * @param {int} hours
     * @returns
     */
    getDaysFromHours(hours) {
      hours = parseFloat(hours)
      if (hours == 0) return 0
      if (hours < 24) return parseFloat(hours).toFixed(2)
      const days = hours / 24
      return parseFloat(days).toFixed(2)
    },

    /**
     *
     * Check if some date is between ranges date
     */
    isBetweenDate(dateCompare, startDate, endDate) {
      return moment(dateCompare).isBetween(moment(startDate), moment(endDate))
    },

    /**
     * Check if hours is day or not
     * @param {int} hours
     * @returns
     */
    isDaysOrHours(hours) {
      hours = parseFloat(hours)
      if (hours == 0) return 'Jam'
      if (hours < 24) return 'Jam'

      return 'Hari'
    },

    /**
     * Convert time format to seconds integer
     * @param {int} hours
     * @returns
     */
    convertToSeconds(time) {
      if (time == null || time == '') return 0
      let timeArr = time.split(':')
      timeArr = timeArr.map((item) => parseInt(item))

      const hours = 3600 * timeArr[0]
      const minutes = 60 * timeArr[1]

      return hours + minutes + timeArr[2]
    },

    /**
     * Convert seconds integer to Time Format
     * @param {int} hours
     * @returns
     */
    convertToTime(time) {
      if (!time) {
        return '00:00:00'
      }
      const sec_num = parseInt(time, 10)
      let hours = sec_num > 0 ? Math.floor(sec_num / 3600) : Math.ceil(sec_num / 3600)
      let minutes =
        sec_num > 0
          ? Math.floor((sec_num - hours * 3600) / 60)
          : Math.ceil((sec_num - hours * 3600) / 60)
      let seconds = sec_num - hours * 3600 - minutes * 60

      if (sec_num < 0) {
        if (hours > -10) {
          hours = '-0' + hours
        }
        if (minutes > -10) {
          minutes = '-0' + minutes
        }
        if (seconds > -10) {
          seconds = '-0' + seconds
        }
      } else {
        if (hours < 10) {
          hours = '0' + hours
        }
        if (minutes < 10) {
          minutes = '0' + minutes
        }
        if (seconds < 10) {
          seconds = '0' + seconds
        }
      }
      return hours + ':' + minutes + ':' + seconds
    },

    /**
     * Convert seconds integer to Time Format
     * @param {int} hours
     * @returns
     */
    convertTimeToString(time) {
      if (!time) {
        return (
          `0${i18n.t('global.time.hourAlias')} ` +
          `0${i18n.t('global.time.minuteAlias')} ` +
          `0${i18n.t('global.time.secondAlias')}`
        )
      }
      const sec_num = parseInt(time, 10)
      let hours = sec_num > 0 ? Math.floor(sec_num / 3600) : Math.ceil(sec_num / 3600)
      let minutes =
        sec_num > 0
          ? Math.floor((sec_num - hours * 3600) / 60)
          : Math.ceil((sec_num - hours * 3600) / 60)
      let seconds = sec_num - hours * 3600 - minutes * 60

      if (sec_num < 0) {
        if (hours > -10) {
          hours = '-0' + hours
        }
        if (minutes > -10) {
          minutes = '-0' + minutes
        }
        if (seconds > -10) {
          seconds = '-0' + seconds
        }
      } else {
        if (hours < 10) {
          hours = '0' + hours
        }
        if (minutes < 10) {
          minutes = '0' + minutes
        }
        if (seconds < 10) {
          seconds = '0' + seconds
        }
      }
      return (
        hours +
        `${i18n.t('global.time.hourAlias')} ` +
        minutes +
        `${i18n.t('global.time.minuteAlias')} ` +
        seconds +
        `${i18n.t('global.time.secondAlias')}`
      )
    },

    calculateDate(date1, date2, type) {
      date1 = moment(date1)
      date2 = moment(date2)

      return Number(date1.diff(date2, type))
    },

    /**
     * to Calculate Day from two date
     * @param {int} hours
     * @returns
     */
    calculateDay(date1, date2) {
      date1 = moment(date1)
      date2 = moment(date2)

      let diff = date1.diff(date2, 'days')

      if (diff < 0) diff *= -1

      return diff + 1
    },

    /**
     * to Calculate Day from date to now
     * @param {int} hours
     * @returns
     */
    calculateHours(date2) {
      let date1 = moment()
      date2 = moment(date2)

      let diff = date1.diff(date2, 'hours')

      if (diff < 0) diff *= -1

      return diff + 1
    },

    /**
     * to Calculate Day from date to now
     * @param {int} hours
     * @returns
     */
    calculateMinutes(date1, date2) {
      date1 = moment(date1)
      date2 = moment(date2)
      let diff = date1.diff(date2, 'minutes')

      if (diff < 0) diff *= -1

      return diff + 1
    },

    /**
     *
     * @param {*} dateStr localDate which will convert to utc date
     * @param {*} offset offset hours
     * @returns
     */
    getUtcDateFromLocalDate(dateStr, offset) {
      return moment(dateStr)
        .utcOffset(offset * 60, true)
        .toISOString()
    },

    /**
     *
     * @param {*} utcDateStr
     * @param {*} offset
     * @returns
     */
    convertUtcToLocalDate(utcDateStr, offset) {
      // Parse the UTC date string
      const utcMoment = moment.utc(utcDateStr)

      // Apply the offset (in hours) to get the local time
      // The false parameter means "keep the UTC time and adjust the offset"
      const localMoment = utcMoment.utcOffset(offset * 60, false)

      // Format the result (adjust the format as needed)
      return localMoment.format('YYYY-MM-DDTHH:mm:ss')
    },
    /**
     * user activity log
     * @param {*} action
     * @param {*} data
     */
    userLogging(action, data, component = null) {
      if (!data || !data.path) {
        return
      }

      let path = data.path
      const params = data.params ? Object.values(data.params) : []

      params.forEach((p) => {
        if (p) {
          path = path.replace(`/${p}`, '')
        }
      })

      const input = {
        activity: action,
        path: path,
        tabName: data.name || null,
      }

      if (component) {
        input.component = component
      }

      client.mutate({
        mutation: LOGGING,
        variables: {
          input,
        },
      })
    },

    /**
     * Duration to minutes
     */
    durationToMinutes(duration, durationOption) {
      const optionValue = {
        Menit: 1,
        Jam: 60,
        Hari: 1440,
        Minggu: 10080,
        Bulan: 43800,
      }

      return parseInt(duration) * optionValue[durationOption]
    },

    /**
     * Get Duration Option
     */
    getDurationOption(duration) {
      const optionValue = {
        Bulan: 43800,
        Minggu: 10080,
        Hari: 1440,
        Jam: 60,
        Menit: 1,
      }
      const keys = Object.keys(optionValue)
      let result = 'Menit'
      let resultValue = 0

      for (let index = 0; index < keys.length; index++) {
        if (duration % optionValue[keys[index]] === 0) {
          result = keys[index]
          resultValue = duration / optionValue[keys[index]]
          break
        }
      }

      return { value: resultValue, option: result }
    },

    isNumber(event) {
      event = event ? event : window.event
      const charCode = event.which ? event.which : event.keyCode
      if ((charCode >= 48 && charCode <= 57) || charCode == 46) {
        return true
      } else {
        event.preventDefault()
      }
    },

    isNumeric(event) {
      event = event ? event : window.event
      const charCode = event.which ? event.which : event.keyCode

      if (charCode >= 48 && charCode <= 57) {
        return true
      } else {
        event.preventDefault()
      }
    },

    isDecimal(event) {
      event = event ? event : window.event
      const charCode = event.which ? event.which : event.keyCode

      if (charCode >= 48 && charCode <= 57) {
        return true
      }

      const currentValue = event.target.value
      if (charCode == 46 && !currentValue.includes('.')) {
        return true
      }

      event.preventDefault()
    },

    isPhoneNumber(event) {
      event = event ? event : window.event
      const charCode = event.which ? event.which : event.keyCode
      if (
        (charCode >= 48 && charCode <= 57) ||
        charCode == 40 ||
        charCode == 41 ||
        charCode == 45
      ) {
        return true
      } else {
        event.preventDefault()
      }
    },

    isPasteNumber(event) {
      const text = event.clipboardData.getData('text')
      const conditions = ['e', 'E', '-', '.', ',']
      if (conditions.some((val) => text.includes(val))) {
        event.preventDefault()
      }
    },

    isPastePhoneNumber(event) {
      var text = event.clipboardData.getData('text')
      const conditions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '(', ')', '-']
      for (let i = 0; i < text.length; i++) {
        var state = false
        for (let j = 0; j < conditions.length; j++) {
          if (conditions[j] == text[i]) {
            state = true
            break
          }
        }
        if (state == false) {
          event.preventDefault()
          break
        }
      }
    },

    getCenter(arr) {
      var minX, maxX, minY, maxY
      for (var i = 0; i < arr.length; i++) {
        minX = arr[i].lat < minX || minX == null ? arr[i].lat : minX
        maxX = arr[i].lat > maxX || maxX == null ? arr[i].lat : maxX
        minY = arr[i].lng < minY || minY == null ? arr[i].lng : minY
        maxY = arr[i].lng > maxY || maxY == null ? arr[i].lng : maxY
      }
      // return [(minX + maxX) / 2, (minY + maxY) / 2];
      return {
        latitude: (minX + maxX) / 2,
        longitude: (minY + maxY) / 2,
      }
    },

    getDayName(date) {
      return new Date(date).toLocaleDateString('id-ID', { weekday: 'long' })
    },

    convertPeriodName(en) {
      if (en === 'd') return 'Hari'
      else if (en === 'w') return 'Minggu'
      else if (en === 'm') return 'Bulan'
      else if (en === 'y') return 'Tahun'
    },

    numberIdFormatter(value) {
      return value.split(/ /)[0].replace(/[^,0-9]/g, '')
    },

    motionStatusFormat(param) {
      let label
      switch (param) {
        case 'M':
          label = i18n.t('liveView.filter.moving')
          break
        case 'P':
          label = i18n.t('liveView.filter.parking')
          break
        case 'I':
          label = i18n.t('liveView.filter.idle')
          break
        case 'A':
          label = i18n.t('liveView.filter.assisted')
          break
        case 'O':
          label = i18n.t('liveView.filter.offline')
          break
        case 'U':
          label = i18n.t('liveView.filter.unknown')
          break
        default:
          break
      }

      return label
    },

    motionStatusColor(motion) {
      let color = 'secondary'
      switch (motion) {
        case 'M':
          color = 'success'
          break
        case 'I':
          color = 'warning'
          break
        case 'P':
          color = 'danger'
          break
        case 'A':
          color = 'info'
          break
        default:
          color = 'secondary'
          break
      }

      return color
    },

    integerFormatter(value) {
      return value.split(/ /)[0].replace(/\D/g, '')
    },

    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },

    formattedPeriode(date) {
      return moment(date).local().format('YYYY-MM-DDTHH:mm:ss')
    },

    formatDateToUtc(date) {
      return moment(date).utc().toISOString()
    },

    isUsingCache() {
      const companyId = this.getCompanyId()
      const featureFlags = store.state.identity.featureFlag

      if (!Array.isArray(featureFlags)) return false

      const whitelistFeature = featureFlags.find(
        (feature) => feature.key == 'cache_geolocation_whitelist'
      )
      const cacheGeolocationFeature = featureFlags.find(
        (feature) => feature.key == 'cache_geolocation'
      )

      if (!whitelistFeature?.value || cacheGeolocationFeature?.value === undefined) return false

      let whitelist = []
      try {
        whitelist = JSON.parse(whitelistFeature.value)
      } catch (error) {
        return false
      }
      const cacheGeolocation = cacheGeolocationFeature.value
      const isCache =
        cacheGeolocation &&
        companyId !== undefined &&
        (whitelist.length === 0 || (whitelist.length > 0 && whitelist.includes(companyId)))

      return isCache
    },

    nowUtc() {
      return moment().utc().toISOString()
    },

    /**
     * Get duration Object between two dates in minutes
     * @param {*} date1
     * @param {*} date2
     * @returns minutes
     */
    getDurationBetween2Dates(date1, date2) {
      return moment.duration(moment(date2).diff(moment(date1))).asMinutes()
    },

    /**
     * Get ranges this day start from 00:00 - now
     *
     */
    getRangesThisDay() {
      return [
        moment(new Date()).format('YYYY-MM-DD 00:00'),
        moment(new Date()).format('YYYY-MM-DD HH:mm'),
      ]
    },

    resizeImage(image, height, width) {
      if (image) {
        image.style.height = height + 'px'
        image.onload = () => {
          const ratio = image.width / image.height
          if (ratio > 1 && image.width > width) {
            image.style.width = width + 'px'
            image.style.height = 'auto'
          }
        }
      }
    },

    convertSecondsToTime(seconds) {
      if (seconds === null || isNaN(seconds)) {
        return '-'
      }
      const getLanguage = localStorage.getItem('language') ? localStorage.getItem('language') : 'id'
      const days = Math.floor(seconds / (24 * 3600))
      seconds %= 24 * 3600
      const hours = Math.floor(seconds / 3600)
      seconds %= 3600
      const minutes = Math.floor(seconds / 60)
      seconds %= 60

      const addLeadingZero = (value) => (value < 10 ? '0' + value : value)

      if (days >= 2) {
        const t = getLanguage === 'en' ? ' Days ' : ' Hari '
        return (
          days +
          t +
          addLeadingZero(hours) +
          ':' +
          addLeadingZero(minutes) +
          ':' +
          addLeadingZero(seconds)
        )
      } else if (days >= 1) {
        const t = getLanguage === 'en' ? ' Day ' : ' Hari '
        return (
          days +
          t +
          addLeadingZero(hours) +
          ':' +
          addLeadingZero(minutes) +
          ':' +
          addLeadingZero(seconds)
        )
      } else {
        return addLeadingZero(hours) + ':' + addLeadingZero(minutes) + ':' + addLeadingZero(seconds)
      }
    },

    addLeadingZero(value) {
      return value < 10 ? '0' + value : value
    },

    isObject(v) {
      return !!v && v.constructor === Object
    },

    getStatusVideo(item) {
      const createdOn = (moment() - moment(item.createdOn)) / 1000 / 60
      const videoSrc = item.formattedAlertData?.includes('https') ? item.formattedAlertData : null
      if (videoSrc && videoSrc != '-') {
        return 'uploaded'
      } else if ((!videoSrc || videoSrc == '-') && createdOn <= 30) {
        return 'pending'
      } else {
        return 'failed'
      }
    },

    devideJob(array, devideRowCount) {
      if (array.length <= devideRowCount) {
        return [array]
      }
      const result = []
      const length = array.length
      const devideResult = Math.ceil(length / devideRowCount)
      for (let i = 0; i < devideResult; i++) {
        const start = i * devideRowCount
        const end = start + devideRowCount
        result.push(array.slice(start, end))
      }
      return result
    },

    async generateAddress(address, data, type, column = []) {
      const newAddress = []
      const latLng = []
      const addresses = new Map()
      const currentLatLng = new Map()
      let devideJob = []
      let limitCount = 50
      const useCache = this.isUsingCache()
      if (type == 'vehicle-trip' || type == 'summary-engine' || type == 'overspeed') {
        limitCount = 25
      }
      if (data.length > limitCount) {
        devideJob = this.devideJob(data, limitCount)
      } else {
        devideJob = [data]
      }
      for (let [indexData, itemData] of devideJob.entries()) {
        itemData.forEach((item, index) => {
          index = indexData * limitCount + index
          if (type == 'vehicle-trip') {
            if (column.includes('startAddress')) {
              const addr1 = address.find(
                (add) =>
                  add.latitude == item.reportVehicleTrip.startLat &&
                  add.longitude == item.reportVehicleTrip.startLong
              )
              if (addr1) {
                data[index].reportVehicleTrip.startAddress = addr1.address
              } else {
                latLng.push({
                  latitude: item.reportVehicleTrip.startLat,
                  longitude: item.reportVehicleTrip.startLong,
                })
              }
            }
            if (column.includes('endAddress')) {
              const addr2 = address.find(
                (add) =>
                  add.latitude == item.reportVehicleTrip.stopLat &&
                  add.longitude == item.reportVehicleTrip.stopLong
              )

              if (addr2) {
                data[index].reportVehicleTrip.stopAddress = addr2.address
              } else {
                latLng.push({
                  latitude: item.reportVehicleTrip.stopLat,
                  longitude: item.reportVehicleTrip.stopLong,
                })
              }
            }
          } else if (
            type == 'summary-engine' ||
            type == 'overspeed' ||
            type == 'historical-offline'
          ) {
            if (column.includes('startLocation')) {
              if (type == 'summary-engine') {
                const addr1 = address.find(
                  (add) =>
                    add.latitude == item.startLocation.lat &&
                    add.longitude == item.startLocation.lon
                )
                if (addr1) {
                  data[index].startLocation.displayName = addr1.address
                } else {
                  latLng.push({
                    latitude: item.startLocation.lat,
                    longitude: item.startLocation.lon,
                  })
                }
              } else if (type == 'overspeed' || type == 'historical-offline') {
                const addr1 = address.find(
                  (add) =>
                    add.latitude == item.startLatitude && add.longitude == item.startLongitude
                )
                if (addr1) {
                  data[index].startLocation = addr1.address
                } else {
                  latLng.push({
                    latitude: item.startLatitude,
                    longitude: item.startLongitude,
                  })
                }
              }
            }
            if (column.includes('stopLocation')) {
              if (type == 'summary-engine') {
                const addr2 = address.find(
                  (add) =>
                    add.latitude == item.stopLocation.lat && add.longitude == item.stopLocation.lon
                )

                if (addr2) {
                  data[index].stopLocation.displayName = addr2.address
                } else {
                  latLng.push({
                    latitude: item.stopLocation.lat,
                    longitude: item.stopLocation.lon,
                  })
                }
              } else if (type == 'overspeed') {
                const addr2 = address.find(
                  (add) => add.latitude == item.endLatitude && add.longitude == item.endLongitude
                )

                if (addr2) {
                  data[index].endLocation = addr2.address
                } else {
                  latLng.push({
                    latitude: item.endLatitude,
                    longitude: item.endLongitude,
                  })
                }
              } else {
                const addr2 = address.find(
                  (add) => add.latitude == item.stopLatitude && add.longitude == item.stopLongitude
                )

                if (addr2) {
                  data[index].stopLocation = addr2.address
                } else {
                  latLng.push({
                    latitude: item.stopLatitude,
                    longitude: item.stopLongitude,
                  })
                }
              }
            }
          } else if (type == 'summary-sensor-ciomas') {
            if (column.includes('startLocation')) {
              const addr = address.find(
                (add) =>
                  add.latitude == item.startTrip?.latitude &&
                  add.longitude == item.startTrip?.longitude
              )
              if (addr) {
                Vue.set(data[index], 'startAddress', addr.address)
              } else if (item.startTrip?.latitude && item.startTrip?.longitude) {
                latLng.push({
                  latitude: item.startTrip.latitude,
                  longitude: item.startTrip.longitude,
                })
              }
            }
            if (column.includes('stopLocation')) {
              const addr = address.find(
                (add) =>
                  add.latitude == item.endTrip?.latitude && add.longitude == item.endTrip?.longitude
              )
              if (addr) {
                Vue.set(data[index], 'stopAddress', addr.address)
              } else if (item.endTrip?.latitude && item.endTrip?.longitude) {
                latLng.push({
                  latitude: item.endTrip.latitude,
                  longitude: item.endTrip.longitude,
                })
              }
            }
          } else if (type == 'detail-vehicle-trip') {
            const addr = address.find(
              (add) => add.latitude == item.latitude && add.longitude == item.longitude
            )
            if (addr) {
              data[index].reportDetailVehicleTrip.address = addr.address
            } else {
              latLng.push({
                latitude: item.reportDetailVehicleTrip.latitude,
                longitude: item.reportDetailVehicleTrip.longitude,
              })
            }
          } else {
            const addr = address.find(
              (add) => add.latitude == item.latitude && add.longitude == item.longitude
            )
            if (addr) {
              if (type == 'vehicle-offline') {
                data[index].lastLocation = addr.address
              } else {
                data[index].address = addr.address
              }
            } else {
              latLng.push({
                latitude: item.latitude,
                longitude: item.longitude,
              })
            }
          }
        })
        const coords = []

        latLng.forEach((item) => {
          const lat = Number(item.latitude)
          const lon = Number(item.longitude)
          const keyData = lat + '&' + lon
          const getLatLng = currentLatLng.get(keyData)
          if (!getLatLng) {
            coords.push({ lat, lon })
            currentLatLng.set(keyData, true)
          }
        })

        if (coords.length > 0) {
          if (useCache) {
            const tempAddresses = await getGeolocation(coords)

            for (const [key, value] of tempAddresses) {
              addresses.set(key.replace(',', '&'), value.name === '' ? '-' : value.name)
            }
          } else {
            const result = await Promise.all(
              coords.map((coord) => GetAddressData(coord.lat, coord.lon))
            )

            for (const res of result) {
              const address = res.display_name
              const keyData = res.lat + '&' + res.lon
              addresses.set(keyData, address)
            }
          }
        }

        if (itemData.length > 0) {
          itemData.forEach((item, index) => {
            index = indexData * limitCount + index
            if (type == 'vehicle-trip') {
              if (
                !item.reportVehicleTrip.startAddress &&
                item.reportVehicleTrip.startLat !== null &&
                item.reportVehicleTrip.startLat !== '' &&
                item.reportVehicleTrip.startLong !== null &&
                item.reportVehicleTrip.startLong !== ''
              ) {
                const keyData =
                  item.reportVehicleTrip.startLat + '&' + item.reportVehicleTrip.startLong
                const addr = addresses.get(keyData)
                if (addr) {
                  data[index].reportVehicleTrip.startAddress = addr
                  if (
                    addr != '-' ||
                    item.reportVehicleTrip.startLat === 0 ||
                    item.reportVehicleTrip.startLong === 0
                  ) {
                    newAddress.push({
                      latitude: item.reportVehicleTrip.startLat,
                      longitude: item.reportVehicleTrip.startLong,
                      address: addr,
                    })
                  }
                } else {
                  data[index].reportVehicleTrip.startAddress = '-'
                }
              } else if (!item.reportVehicleTrip.startAddress) {
                data[index].reportVehicleTrip.startAddress = '-'
              }
              if (
                !item.reportVehicleTrip.stopAddress &&
                item.reportVehicleTrip.stopLat !== null &&
                item.reportVehicleTrip.stopLat !== '' &&
                item.reportVehicleTrip.stopLong !== null &&
                item.reportVehicleTrip.stopLong !== ''
              ) {
                const keyData =
                  item.reportVehicleTrip.stopLat + '&' + item.reportVehicleTrip.stopLong
                const addr = addresses.get(keyData)
                if (
                  addr != '-' ||
                  item.reportVehicleTrip.stopLat === 0 ||
                  item.reportVehicleTrip.stopLong === 0
                ) {
                  data[index].reportVehicleTrip.stopAddress = addr
                  newAddress.push({
                    latitude: item.reportVehicleTrip.stopLat,
                    longitude: item.reportVehicleTrip.stopLong,
                    address: addr,
                  })
                } else {
                  data[index].reportVehicleTrip.stopAddress = '-'
                }
              } else if (!item.reportVehicleTrip.stopAddress) {
                data[index].reportVehicleTrip.stopAddress = '-'
              }
            } else if (type == 'summary-engine') {
              if (
                !item.startLocation.displayName &&
                item.startLocation.lat !== null &&
                item.startLocation.lat !== '' &&
                item.startLocation.lon !== null &&
                item.startLocation.lon !== ''
              ) {
                const keyData = item.startLocation.lat + '&' + item.startLocation.lon
                const addr = addresses.get(keyData)
                if (addr != '-' || item.startLocation.lat === 0 || item.startLocation.lon === 0) {
                  data[index].startLocation.displayName = addr
                  newAddress.push({
                    latitude: item.startLocation.lat,
                    longitude: item.startLocation.lon,
                    address: addr,
                  })
                } else {
                  data[index].startLocation.displayName = '-'
                }
              } else if (!item.startLocation.displayName) {
                data[index].startLocation.displayName = '-'
              }
              if (
                !item.stopLocation.displayName &&
                item.stopLocation.lat !== null &&
                item.stopLocation.lat !== '' &&
                item.stopLocation.lon !== null &&
                item.stopLocation.lon !== ''
              ) {
                const keyData = item.stopLocation.lat + '&' + item.stopLocation.lon
                const addr = addresses.get(keyData)
                if (addr != '-' || item.stopLocation.lat === 0 || item.stopLocation.lon === 0) {
                  data[index].stopLocation.displayName = addr
                  newAddress.push({
                    latitude: item.stopLocation.lat,
                    longitude: item.stopLocation.lon,
                    address: addr,
                  })
                } else {
                  data[index].stopLocation.displayName = '-'
                }
              } else if (!item.stopLocation.displayName) {
                data[index].stopLocation.displayName = '-'
              }
            } else if (type == 'overspeed') {
              if (
                !item.startLocation &&
                item.startLatitude !== null &&
                item.startLatitude !== '' &&
                item.startLongitude !== null &&
                item.startLongitude !== ''
              ) {
                const keyData = item.startLatitude + '&' + item.startLongitude
                const addr = addresses.get(keyData)
                if (addr != '-' || item.startLatitude === 0 || item.startLongitude === 0) {
                  data[index].startLocation = addr
                  newAddress.push({
                    latitude: item.startLatitude,
                    longitude: item.startLongitude,
                    address: addr,
                  })
                } else {
                  data[index].startLocation = '-'
                }
              } else if (!item.startLocation) {
                data[index].startLocation = '-'
              }
              if (
                !item.endLocation &&
                item.endLatitude !== null &&
                item.endLatitude !== '' &&
                item.endLongitude !== null &&
                item.endLongitude !== ''
              ) {
                const keyData = item.endLatitude + '&' + item.endLongitude
                const addr = addresses.get(keyData)
                if (addr != '-' || item.endLatitude === 0 || item.endLongitude === 0) {
                  data[index].endLocation = addr
                  newAddress.push({
                    latitude: item.endLatitude,
                    longitude: item.endLongitude,
                    address: addr,
                  })
                } else {
                  data[index].endLocation = '-'
                }
              } else if (!item.endLocation) {
                data[index].endLocation = '-'
              }
            } else if (type == 'historical-offline') {
              if (
                !item.startLocation &&
                item.startLatitude !== null &&
                item.startLatitude !== '' &&
                item.startLongitude !== null &&
                item.startLongitude !== ''
              ) {
                const keyData = item.startLatitude + '&' + item.startLongitude
                const addr = addresses.get(keyData)
                if (addr != '-' || item.startLatitude === 0 || item.startLongitude === 0) {
                  data[index].startLocation = addr
                  newAddress.push({
                    latitude: item.startLatitude,
                    longitude: item.startLongitude,
                    address: addr,
                  })
                } else {
                  data[index].startLocation = '-'
                }
              } else if (!item.startLocation) {
                data[index].startLocation = '-'
              }
              if (
                !item.stopLocation &&
                item.stopLatitude !== null &&
                item.stopLatitude !== '' &&
                item.stopLongitude !== null &&
                item.stopLongitude !== ''
              ) {
                const keyData = item.stopLatitude + '&' + item.stopLongitude
                const addr = addresses.get(keyData)
                if (addr != '-' || item.stopLatitude === 0 || item.stopLongitude === 0) {
                  data[index].stopLocation = addr
                  newAddress.push({
                    latitude: item.stopLatitude,
                    longitude: item.stopLongitude,
                    address: addr,
                  })
                } else {
                  data[index].stopLocation = '-'
                }
              } else if (!item.stopLocation) {
                data[index].stopLocation = '-'
              }
            } else if (type == 'summary-sensor-ciomas') {
              if (!data[index].startAddress) {
                if (
                  item.startTrip?.latitude !== null &&
                  item.startTrip?.latitude !== '' &&
                  item.startTrip?.longitude !== null &&
                  item.startTrip?.longitude !== ''
                ) {
                  const keyData = item.startTrip.latitude + '&' + item.startTrip.longitude
                  const addr = addresses.get(keyData)
                  if (
                    addr != '-' ||
                    item.startTrip.latitude === 0 ||
                    item.startTrip.longitude === 0
                  ) {
                    Vue.set(data[index], 'startAddress', addr)
                    newAddress.push({
                      latitude: item.startTrip.latitude,
                      longitude: item.startTrip.longitude,
                      address: addr,
                    })
                  } else {
                    Vue.set(data[index], 'startAddress', '-')
                  }
                } else {
                  Vue.set(data[index], 'startAddress', '-')
                }
              }
              if (!data[index].stopAddress) {
                if (
                  item.endTrip?.latitude !== null &&
                  item.endTrip?.latitude !== '' &&
                  item.endTrip?.longitude !== null &&
                  item.endTrip?.longitude !== ''
                ) {
                  const keyData = item.endTrip.latitude + '&' + item.endTrip.longitude
                  const addr = addresses.get(keyData)
                  if (addr != '-' || item.endTrip.latitude === 0 || item.endTrip.longitude === 0) {
                    Vue.set(data[index], 'stopAddress', addr)
                    newAddress.push({
                      latitude: item.endTrip.latitude,
                      longitude: item.endTrip.longitude,
                      address: addr,
                    })
                  } else {
                    Vue.set(data[index], 'stopAddress', '-')
                  }
                } else {
                  Vue.set(data[index], 'stopAddress', '-')
                }
              }
            } else if (type == 'detail-vehicle-trip') {
              if (
                item.reportDetailVehicleTrip.latitude !== null &&
                item.reportDetailVehicleTrip.latitude !== '' &&
                item.reportDetailVehicleTrip.longitude !== null &&
                item.reportDetailVehicleTrip.longitude !== ''
              ) {
                const keyData =
                  item.reportDetailVehicleTrip.latitude +
                  '&' +
                  item.reportDetailVehicleTrip.longitude
                const addr = addresses.get(keyData)
                if (
                  addr != '-' ||
                  item.reportDetailVehicleTrip.latitude === 0 ||
                  item.reportDetailVehicleTrip.longitude === 0
                ) {
                  data[index].reportDetailVehicleTrip.address = addr
                  newAddress.push({
                    latitude: item.reportDetailVehicleTrip.latitude,
                    longitude: item.reportDetailVehicleTrip.longitude,
                    address: addr,
                  })
                } else {
                  data[index].reportDetailVehicleTrip.address = '-'
                }
              } else {
                data[index].reportDetailVehicleTrip.address = '-'
              }
            } else {
              if (
                item.latitude !== null &&
                item.latitude !== '' &&
                item.longitude !== null &&
                item.longitude !== ''
              ) {
                if (type == 'vehicle-offline' && !item.lastLocation) {
                  const keyData = item.latitude + '&' + item.longitude
                  const addr = addresses.get(keyData)
                  if (addr != '-' || item.latitude === 0 || item.longitude === 0) {
                    data[index].lastLocation = addr
                    newAddress.push({
                      latitude: item.latitude,
                      longitude: item.longitude,
                      address: addr,
                    })
                  } else {
                    data[index].lastLocation = '-'
                  }
                } else if (type != 'vehicle-offline' && !item.address) {
                  const keyData = item.latitude + '&' + item.longitude
                  const addr = addresses.get(keyData)
                  if (addr != '-' || item.latitude === 0 || item.longitude === 0) {
                    data[index].address = addr
                    newAddress.push({
                      latitude: item.latitude,
                      longitude: item.longitude,
                      address: addr,
                    })
                  } else {
                    data[index].address = '-'
                  }
                }
              } else {
                if (type == 'vehicle-offline' && !item.lastLocation) {
                  data[index].lastLocation = '-'
                } else if (type != 'vehicle-offline' && !item.address) {
                  data[index].address = '-'
                }
              }
            }
          })
        }
      }
      return { data, newAddress }
    },

    momentDay(date) {
      const getLanguage = localStorage.getItem('language') ? localStorage.getItem('language') : 'id'
      moment.locale(getLanguage)
      return !date ? '-' : moment(date).format('dddd')
    },

    convertMinutesToTime(minutes) {
      const getLanguage = localStorage.getItem('language') ? localStorage.getItem('language') : 'id'
      if (!minutes || isNaN(minutes)) return '-'

      const translations = {
        id: {
          year: 'tahun',
          month: 'bulan',
          day: 'hari',
          hour: 'jam',
          minute: 'menit',
          second: 'detik',
        },
      }

      const timeUnits = [
        { label: 'year', minutes: 525600 },
        { label: 'month', minutes: 43800 },
        { label: 'day', minutes: 1440 },
        { label: 'hour', minutes: 60 },
        { label: 'minute', minutes: 1 },
      ]

      const time = timeUnits.reduce((result, unit) => {
        const value = Math.floor(minutes / unit.minutes)
        minutes %= unit.minutes

        const translatedLabel =
          getLanguage === 'id' ? translations['id'][unit.label] || unit.label : unit.label
        const pluralSuffix = getLanguage === 'en' && value !== 1 ? 's' : ''

        if (value > 0) {
          result.push(value + ` ${translatedLabel}${pluralSuffix}`)
        }
        return result
      }, [])

      return time.length ? time.join(' ') : '-'
    },

    validateFilter(reportFilter, filter) {
      const getMessage = (key) => {
        if (key == 'periode') {
          messageError = `${i18n.t('report.object.reportPeriod')} ${i18n.t(
            'global.validation.required'
          )}`
        } else if (key == 'vehicleId') {
          messageError = `${i18n.t('report.fields.licensePlate')} ${i18n.t(
            'global.validation.required'
          )}`
        } else if (key == 'driverId') {
          messageError = `${i18n.t('report.fields.driver')} ${i18n.t('global.validation.required')}`
        } else if (key == 'alertCode') {
          messageError = `${i18n.t('report.fields.alertType')} ${i18n.t(
            'global.validation.required'
          )}`
        } else if (key == 'garageId') {
          messageError = `${i18n.t('report.fields.garage')} ${i18n.t('global.validation.required')}`
        } else if (key == 'startGeofenceId') {
          messageError = `${i18n.t('report.fields.startGeofence')} ${i18n.t(
            'global.validation.required'
          )}`
        } else if (key == 'endGeofenceId') {
          messageError = `${i18n.t('report.fields.stopGeofence')} ${i18n.t(
            'global.validation.required'
          )}`
        }
        return messageError
      }
      let messageError = ''
      if (Object.keys(filter).length > 0) {
        for (const item of Object.keys(filter)) {
          if (
            reportFilter[item] &&
            reportFilter[item].required &&
            (!filter[item] || filter[item].length <= 0)
          ) {
            messageError = getMessage(item)
            break
          }
        }
      } else {
        for (const item of Object.keys(reportFilter)) {
          if (reportFilter[item] && reportFilter[item].required) {
            messageError = getMessage(item)
            break
          }
        }
      }

      if (messageError.length > 0) {
        return { valid: false, msg: messageError }
      }
      return { valid: true, msg: '' }
    },

    convertDate(dateString) {
      const formattedDate = moment(dateString).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
      return formattedDate
    },
    /**
     * Convert query params to string, and support filters param
     * @param {Object} params
     * @returns String
     */
    queryParamsToString(params) {
      let queries = []
      for (const [key, val] of Object.entries(params)) {
        if (key === 'filters') {
          val.forEach((filter) => {
            queries.push(`filters[]=${filter}`)
          })
        } else queries.push(`${key}=${val}`)
      }
      return (queries = queries.join('&'))
    },
    /**
     *
     * @param {Object} errObj
     * @param {Object} errMessagelist
     * @param {String} replacedText
     * @param {Object} errorMessageList
     * @param {Array} errorKeys
     * @param {Boolean} isRaw
     */
    withErrorHandler(errObj = {}, errorMessageList = {}, errorKeys = [], isRaw = false) {
      const { errors = {}, replacedText = {} } = errObj
      if (Object.keys(errors).length === 0) return i18n.t('global.somethingWentWrong')
      const fieldErrorKeys = Object.keys(errors)
      let errorMessage = ''
      let errorMessageArr = []
      // Create a map to store merged objects by errorMessage
      const resultMap = new Map()

      fieldErrorKeys.forEach((fieldErrorKey) => {
        let errorList = errors[fieldErrorKey]

        // for object value don't filter
        if (!isRaw)
          errorList = errorList.filter((val, key) => {
            if (typeof val === 'object') return true
            return key === 0
          })

        errorList.forEach((val) => {
          if (typeof val === 'string') {
            /**
             * Error example
             * {
                  "message": "bad_request",
                  "errors": {
                    "delivery_orders": [
                      "customer ids is not equal"
                    ]
                  }
                }
             */
            const msg = errorMessageList[fieldErrorKey][val] || val
            errorMessage = msg ? i18n.t(msg, replacedText) : i18n.t('global.somethingWentWrong')
            errorMessageArr.push(errorMessage)
          } else if (typeof val === 'object') {
            /**
             * Error example
             * {
                  "message": "bad_request",
                  "errors": {
                    "delivery_orders": [
                      {
                        "id": "3ce0c439-a513-4c26-8f8a-63bb93d2e71f",
                        "number": "DO-0008",
                        "invoice_id": [
                          "already invoiced"
                        ]
                      }
                    ]
                  }
                }
             */
            for (const [key, item] of Object.entries(val)) {
              if (errorKeys.includes(key)) {
                const msg = errorMessageList[fieldErrorKey][key][item[0]]
                errorMessage = msg ? i18n.t(msg, replacedText) : i18n.t('global.somethingWentWrong')
              }
            }

            const keymap = errorMessage
            if (resultMap.has(keymap)) {
              // Merge elements with the same invoice_id
              if ('id' in resultMap.get(keymap)) resultMap.get(keymap).id += `,${val.id}`
              if ('number' in resultMap.get(keymap))
                resultMap.get(keymap).number += `,${val.number}`
            } else {
              resultMap.set(keymap, { ...val, errorMessage })
            }
          }
        })

        // Convert the map values back to an array
        const mergedArray = Array.from(resultMap.values())
        errorMessageArr = [...mergedArray, ...errorMessageArr]
      })

      return resultMap.size || isRaw ? errorMessageArr : errorMessage
      // return resultMap.size || isRaw ? errorMessageArr : errorMessageArr[0]
    },
    // This function to save report filter in localStorage
    saveReportFilter(filterValue, finalFilter, key) {
      let reportFilterValue
      let reportFilter
      if (localStorage.getItem('reportFilterValue')) {
        reportFilterValue = JSON.parse(localStorage.getItem('reportFilterValue'))
        reportFilterValue[key] = filterValue
      } else {
        reportFilterValue = {
          [key]: filterValue,
        }
      }

      if (localStorage.getItem('reportFilter')) {
        reportFilter = JSON.parse(localStorage.getItem('reportFilter'))
        reportFilter[key] = finalFilter
      } else {
        reportFilter = {
          [key]: finalFilter,
        }
      }

      localStorage.setItem('reportFilterValue', JSON.stringify(reportFilterValue))
      localStorage.setItem('reportFilter', JSON.stringify(reportFilter))
    },

    // this function to get value of report filter from localStorage
    getFilterReport(key) {
      let reportFilterValue = {}
      let reportFilter = {}
      if (localStorage.getItem('reportFilterValue')) {
        const allReportFilterValue = JSON.parse(localStorage.getItem('reportFilterValue'))
        reportFilterValue = allReportFilterValue[key] || {}
      }

      if (localStorage.getItem('reportFilter')) {
        const allReportFilter = JSON.parse(localStorage.getItem('reportFilter'))
        reportFilter = allReportFilter[key] || {}
      }
      return {
        reportFilterValue,
        reportFilter,
      }
    },

    // This function to set report filter detail
    setReportDetail(key, value) {
      let reportFilterDetail
      if (localStorage.getItem('reportFilterDetail')) {
        reportFilterDetail = JSON.parse(localStorage.getItem('reportFilterDetail'))
        reportFilterDetail[key] = value
      } else {
        reportFilterDetail = {
          [key]: value,
        }
      }
      localStorage.setItem('reportFilterDetail', JSON.stringify(reportFilterDetail))
    },

    // This function to get report filter detail
    getReportDetail(key) {
      let reportFilterDetail = {}
      if (localStorage.getItem('reportFilterDetail')) {
        const allReportFilterDetail = JSON.parse(localStorage.getItem('reportFilterDetail'))
        reportFilterDetail = allReportFilterDetail[key] || {}
      }
      return reportFilterDetail
    },

    // This function to decrypt aes-256-cbc
    decrypt(encryptedHex) {
      const keyHex = 'fcaa7097c49cb91804fcb9f6033d77a49654e1d746bf2f7673bbf9e9631b6192'

      // Convert hex to bytes
      const key = forge.util.hexToBytes(keyHex)
      const encryptedData = forge.util.hexToBytes(encryptedHex)

      // Check lengths
      if (key.length !== 32) {
        // eslint-disable-next-line no-console
        console.error('Key length must be 32 bytes for AES-256.')
      }
      if (encryptedData.length < 16) {
        // eslint-disable-next-line no-console
        console.error('Encrypted data must be at least 16 bytes for IV.')
      }

      // Extract IV (first 16 bytes) and ciphertext (remaining bytes)
      const iv = encryptedData.slice(0, 16)
      const ciphertext = encryptedData.slice(16)

      // Check lengths of extracted parts
      if (iv.length !== 16) {
        // eslint-disable-next-line no-console
        console.error('IV length must be 16 bytes.')
      }
      if (ciphertext.length === 0) {
        // eslint-disable-next-line no-console
        console.error('Ciphertext is empty.')
      }

      const decipher = forge.cipher.createDecipher('AES-CBC', forge.util.createBuffer(key))
      decipher.start({ iv: forge.util.createBuffer(iv) })
      decipher.update(forge.util.createBuffer(ciphertext))
      const result = decipher.finish()

      if (result) {
        return decipher.output.toString()
      } else {
        return 'Failed to decrypt the data'
      }
    },

    /**
     * Truncates a given character string to a maximum length, appending an ellipsis if the string is longer than the maximum.
     *
     * @param {string} character - The character string to truncate.
     * @param {number} maxLength - The maximum length of the character string.
     * @returns {string} The truncated character string, with an ellipsis appended if the original string was longer than the maximum length.
     */
    truncateCharacter(character, maxLength) {
      return character.length > maxLength ? character.slice(0, maxLength) + '...' : character
    },

    findFilterValue(filters, key) {
      return filters.find((f) => f.key === key)?.values
    },

    isContains(search, name) {
      const searchContains = RegExp(search ?? '', 'gi').exec(name ?? '')
      return searchContains != null && searchContains.length > 0
    },

    /**
     * Converts an array of vehicle groups into a comma-separated string of their names.
     *
     * @param {Object[]} vgroups - An array of vehicle group objects, each with a `name` property.
     * @returns {string} A comma-separated string of the vehicle group names, or '-' if the input array is empty or null.
     */
    getVehicleGroups(vgroups) {
      if (!vgroups || vgroups.length === 0) return '-'
      const nameGroups = vgroups.map((item) => item.vgroup.name)
      return nameGroups.join(', ')
    },

    validationEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },

    /**
     * Decode the polyline6 format to array of lat longs
     *
     * @param {string} encoded - polyline6 format
     * @returns {array} - array of lat lng arrays
     */
    decodePolyline6(encoded) {
      let index = 0,
        len = encoded.length
      let lat = 0,
        lng = 0
      const coordinates = []

      while (index < len) {
        let b,
          shift = 0,
          result = 0
        do {
          b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (b >= 0x20)
        const deltaLat = result & 1 ? ~(result >> 1) : result >> 1
        lat += deltaLat

        shift = 0
        result = 0
        do {
          b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (b >= 0x20)
        const deltaLng = result & 1 ? ~(result >> 1) : result >> 1
        lng += deltaLng

        coordinates.push({
          lat: lat * 1e-6,
          lng: lng * 1e-6,
        })
      }

      return coordinates
    },

    /**
     * Validates the provided URL string.
     *
     * @param {string} url - The URL to validate.
     * @returns {boolean} - `true` if the URL is valid, `false` otherwise.
     */
    validateUrl(url) {
      return /^(http(s)?:\/\/)(([0-9]{1,3}\.){3}[0-9]{1,3}|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,6})(:[0-9]{1,5})?(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?$/.test(
        url
      )
    },

    checkApiVersion(filterBody, reportKey) {
      const reportInterval = ['temperature', 'fuel', 'detail-history-vehicle']
      const report7daysOrOneVehicle = ['history-vehicle']
      if (reportInterval.includes(reportKey))
        return filterBody.interval && filterBody.interval != 'all'
      else if (report7daysOrOneVehicle.includes(reportKey)) {
        return (
          this.getTotalDayReport(filterBody.periode) > 7 ||
          !this.hasOnlyOneVehicleReport(filterBody.vehicleId)
        )
      }
    },

    getAlertDesc(item) {
      let lang = localStorage.getItem('language')
      if (lang == 'en') {
        return item.descriptionEng
      } else {
        return item.descriptionIdn
      }
    },

    /**
     * Gets the battery variant based on the given battery level.
     *
     * @param {number} level - The battery level, ranging from 0 to 100.
     * @returns {string} The battery variant, either 'danger', 'warning', or 'success'.
     */
    getBatteryVariant(level) {
      if (level <= 20) return 'danger'
      if (level <= 50) return 'warning'
      return 'success'
    },

    /**
     *
     * @param {number} deviceCode deviceCode
     * @returns ENUM 'jc' | 'flespi-howen' | 'vss-howen' |  'bsj'
     */
    getDeviceType(deviceCode) {
      if (DEVICE_CODE_LIST.CONCOX_CAMERA.includes(deviceCode)) return 'jc'
      if (DEVICE_CODE_LIST.FLESPI_HOWEN.includes(deviceCode)) return 'flespi-howen'
      if (DEVICE_CODE_LIST.VSS_HOWEN.includes(deviceCode)) return 'vss-howen'
      if (DEVICE_CODE_LIST.BSJ.includes(deviceCode)) return 'bsj'
      return null
    },

    /**
     * Check if device code is BSJ MDVR
     * @param {number} deviceCode deviceCode
     * @returns {boolean} true if device code is BSJ MDVR, false otherwise
     */
    isDeviceBsjMdvr(deviceCode) {
      return DEVICE_CODE_LIST.BSJ_MDVR.includes(deviceCode)
    },

    /**
     * Checks if the given device code is have register call number.
     * @param {number} deviceCode - The device code to check.
     * @returns {boolean} True if have access to register call number, false otherwise.
     */
    isHaveRegisterCallNumber(deviceCode) {
      const deviceCodeList = [...DEVICE_CODE_LIST.CONCOX, ...DEVICE_CODE_LIST.TELTONIKA]
      return deviceCodeList.includes(deviceCode)
    },

    /**
     * Calculates the total number of days between two dates.
     *
     * @param {Array<string>} periode - An array containing the start and end dates as strings.
     * @returns {number} The total number of days between the start and end dates, inclusive.
     */
    getTotalDayReport(periode) {
      let differenceInDays = 0
      if (periode && periode.length > 0) {
        const startDate = new Date(periode[0])
        const endDate = new Date(periode[1])

        const differenceInMilliseconds = endDate - startDate
        differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24)) + 1
      }
      return differenceInDays
    },
    /**
     * Checks if a given vehicle ID is either a single value or an array with at most one element.
     *
     * @param {string|string[]} vehicleId - The vehicle ID or an array of vehicle IDs.
     * @returns {boolean} `true` if the input is a single value or an array with at most one element, `false` otherwise.
     */
    hasOnlyOneVehicleReport(vehicleId) {
      return !Array.isArray(vehicleId) || (Array.isArray(vehicleId) && vehicleId.length <= 1)
    },

    /**
     * Checks if a given vehicle ID is either a single value or an array with at most one element.
     *
     * @param {string|string[]} vehicleId - The vehicle ID or an array of vehicle IDs.
     * @returns {boolean} `true` if the input is a single value or an array with at most one element, `false` otherwise.
     */
    hasLessThanNFilterReport(filter, n) {
      return !Array.isArray(filter) || (Array.isArray(filter) && filter.length <= n)
    },

    /**
     * Converts a time value in seconds to an array of hours, minutes, and seconds.
     *
     * @param {number} time - The time value in seconds.
     * @returns {number[]} An array containing the hours, minutes, and seconds.
     */
    convertSectoHMS(time) {
      const hour = Math.floor(time / 3600)
      time %= 3600
      const minutes = Math.floor(time / 60)
      time %= 60
      const seconds = Math.floor(time)
      return [hour, minutes, seconds]
    },

    convertMinutetoDay(time) {
      const minutes = parseInt(time)
      const days = Math.floor(minutes / (24 * 60))
      const hours = Math.floor((minutes % (24 * 60)) / 60)
      const remainingMinutes = minutes % 60

      let result = []
      if (days > 0) result.push(`${days} ${this.$t('global.time.day')}`)
      if (hours > 0) result.push(`${hours} ${this.$t('global.time.hour')}`)
      if (remainingMinutes > 0) result.push(`${remainingMinutes} ${this.$t('global.time.minute')}`)

      return result.join(' ')
    },

    /**
     * Sanitizes the given HTML content using DOMPurify.
     *
     * @param {string} content - The HTML content to be sanitized.
     * @returns {string} The sanitized HTML content.
     */
    sanitizeHTML(content) {
      return DOMPurify.sanitize(content)
    },

    formatAccurateErrorMessages(issues = []) {
      const errorsMessage = issues
        .filter((item) => item?.source === 'accurate')
        .map((item) => item?.message)
        .filter(Boolean)
      const urlRegex = /https?:\/\/[^\s`'")]+/
      const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/
      const combinedMessage = errorsMessage.join(' ')
      const urlMatch = combinedMessage.match(urlRegex)
      const extractedUrl = urlMatch ? urlMatch[0] : null
      const getUrlDisplayText = (url) => {
        try {
          return `${new URL(url).origin}/`
        } catch (error) {
          return url
        }
      }
      const errorsMessageHtml = errorsMessage.map((msg) =>
        this.sanitizeHTML(
          msg
            .replace(emailRegex, (email) => `<strong>${email}</strong>`)
            .replace(
              urlRegex,
              (url) =>
                `(<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#0D6EFD;text-decoration:underline;">${getUrlDisplayText(
                  url
                )}</a>)`
            )
        )
      )

      return {
        errorsMessage,
        errorsMessageHtml,
        extractedUrl,
      }
    },

    getLabelTimezone() {
      const offsetMinutes = new Date().getTimezoneOffset()
      const offsetHours = offsetMinutes / -60
      switch (offsetMinutes) {
        case -420: // UTC+7
          return 'WIB'
        case -480: // UTC+8
          return 'WITA'
        case -540: // UTC+9
          return 'WIT'
        default:
          return `(GMT${offsetHours >= 0 ? '+' : ''}${offsetHours})`
      }
    },

    getOnboardingStatus() {
      return store.state.identity.whoami?.onboardingNotifData?.onboardingNotif
    },

    getSetupOnboardingStatus() {
      return store.state.identity.whoami?.onboardingNotifData?.setupNotifCompleted
    },

    // get date's week number
    getWeek(date) {
      const newDate = new Date(date)
      // force update to previous date when the day is sunday, in order to week calculation be more make sense
      if (newDate.getDay() == 0) {
        newDate.setDate(newDate.getDate() - 1)
      }
      const currentDate = typeof newDate === 'object' ? newDate : new Date()
      const januaryFirst = new Date(currentDate.getFullYear(), 0, 1)
      const daysToNextMonday = januaryFirst.getDay() === 1 ? 0 : 7 - januaryFirst.getDay()
      const nextMonday = new Date(
        currentDate.getFullYear(),
        0,
        januaryFirst.getDate() + daysToNextMonday
      )

      return currentDate.getTime() < nextMonday.getTime()
        ? 52
        : currentDate.getTime() > nextMonday.getTime()
        ? Math.ceil((currentDate.getTime() - nextMonday.getTime()) / (24 * 3600 * 1000) / 7)
        : 1
    },

    getTimeDiffHMS(date1, date2) {
      const end = moment(date1)
      const start = moment(date2)
      const duration = moment.duration(end.diff(start))
      const hours = String(Math.floor(duration.asHours())).padStart(2, '0')
      const minutes = String(duration.minutes()).padStart(2, '0')
      const seconds = String(duration.seconds()).padStart(2, '0')
      return `${hours}:${minutes}:${seconds}`
    },

    getStartEndOfWeek(week) {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const januaryFirst = new Date(currentYear, 0, 1)
      const daysToNextMonday = januaryFirst.getDay() === 1 ? 0 : 7 - januaryFirst.getDay()
      const nextMonday = new Date(currentYear, 0, januaryFirst.getDate() + daysToNextMonday + 1)
      const startDate = new Date(nextMonday.getTime() + week * 7 * 24 * 60 * 60 * 1000)
      const endDate = new Date(startDate.getTime() + 6 * 24 * 60 * 60 * 1000)
      return [startDate, endDate]
    },

    setStatusTMS(location, value) {
      if (!value) return ''

      const aliasMap = {
        semua: 'all',
        draf: 'draft',
        dikonfirmasi: 'confirmed',
        dijadwalkan: 'scheduled',
        dijalankan: 'started',
        dijadwalkan_ulang: 'rescheduled',
        dalam_proses: 'inprogress',
        proses_pengecekan: 'verification',
        diambil: 'picked',
        terkirim: 'delivered',
        dibatalkan: 'canceled',
        ambil: 'take',
        kirim: 'send',
        pengambilan: 'taking',
        pengiriman: 'delivery',
        menunggu_vendor: 'waiting_for_vendor',
        ditolak_vendor: 'rejected_by_vendor',
        diterima_vendor: 'accepted_by_vendor',
        transit: 'transit',
        non_transit: 'nonTransit',
        direct: 'direct',
        belum_dijadwalkan: 'nonScheduled',
        sedang_transit: 'inTransit',
        kirim_ke_vendor: 'sendToVendor',
      }

      const normalized = value.toLowerCase().replace(/\s+/g, '_')
      const key = aliasMap[normalized] || normalized

      return i18n.t(`${location}.${key}`) || value
    },

    /**
     * Remove country code from phone number.
     *
     * @param {string} phoneNumber - Phone number with or without country code.
     * @return {string} Phone number without country code or leading zero.
     */
    cleanPhoneSafe(phoneNumber) {
      if (!phoneNumber) return ''

      phoneNumber = String(phoneNumber).trim()

      const countryCodes = [
        '1',
        '7',
        '20',
        '27',
        '30',
        '31',
        '32',
        '33',
        '34',
        '36',
        '39',
        '40',
        '41',
        '43',
        '44',
        '45',
        '46',
        '47',
        '48',
        '49',
        '51',
        '52',
        '53',
        '54',
        '55',
        '56',
        '57',
        '58',
        '60',
        '61',
        '62',
        '63',
        '64',
        '65',
        '66',
        '81',
        '82',
        '84',
        '86',
        '90',
        '91',
        '92',
        '93',
        '94',
        '95',
        '98',
        '211',
        '212',
        '213',
        '216',
        '218',
        '220',
        '221',
        '222',
        '223',
        '224',
        '225',
        '226',
        '227',
        '228',
        '229',
        '230',
        '231',
        '232',
        '233',
        '234',
        '235',
        '236',
        '237',
        '238',
        '239',
        '240',
        '241',
        '242',
        '243',
        '244',
        '245',
        '246',
        '248',
        '249',
        '250',
        '251',
        '252',
        '253',
        '254',
        '255',
        '256',
        '257',
        '258',
        '260',
        '261',
        '262',
        '263',
        '264',
        '265',
        '266',
        '267',
        '268',
        '269',
        '290',
        '291',
        '297',
        '298',
        '299',
      ]

      let cleaned = phoneNumber.replace(/^\+/, '')
      for (let code of countryCodes.sort((a, b) => b.length - a.length)) {
        if (cleaned.startsWith(code)) {
          cleaned = cleaned.slice(code.length)
          break
        }
      }
      return cleaned.replace(/^0/, '')
    },

    // if there will be changes on this helper please also update on Vuex notification function ADD_USER_LOG
    groupLogsByDate(logs) {
      if (!Array.isArray(logs)) return []

      if (logs.length > 0 && logs[0].logs) {
        return logs
      }

      const grouped = logs.reduce((groups, log) => {
        const date = moment(log.createdOn).format('DD MMM YYYY')
        groups[date] = groups[date] || []
        groups[date].push(log)
        return groups
      }, {})

      const sorted = Object.entries(grouped)
        .map(([date, logs]) => ({
          date,
          logs: logs.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)),
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date))

      return sorted
    },

    isValidCoordinate(data) {
      if (!data || !data.coordinate) return false
      const { latitude, longitude } = data.coordinate
      const valid =
        typeof latitude === 'number' &&
        typeof longitude === 'number' &&
        latitude > -90 &&
        latitude < 90 &&
        longitude > -180 &&
        longitude < 180 &&
        !(latitude === 0 && longitude === 0)

      return valid
    },

    downloadFile(url, filename) {
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      link.click()
      link.remove()
    },

    getVariant(alert, urgentScope) {
      let _variant = urgentScope ? (alert?.isUrgent ? 'danger' : 'warning') : 'danger'
      return alert?.isResolved || alert?.isFalseAlert ? 'success' : _variant
    },

    extractPathFromDomain(url) {
      const arr = url.split('?')[0].split('/')
      return arr.slice(3).join('/')
    },

    getStreamingUrl(
      deviceCode,
      imei,
      channel,
      flag,
      streamingEndpoint,
      isHlsTesting,
      isUsingWsFlv = false
    ) {
      let origin = process.env.VUE_APP_RTMP_URL
      if (streamingEndpoint === 'trackvision_indo') {
        origin = process.env.VUE_APP_RTMP_URL_SERVER_INDO
      }
      const deviceType = this.getDeviceType(deviceCode)
      if (deviceType === 'jc') {
        return isHlsTesting
          ? `${process.env.VUE_APP_HLS_URL.replace('{numFlag}', flag)}/live/${channel}/${imei}.m3u8`
          : `${origin.replace('{numFlag}', flag)}/live/${channel}/${imei}.flv`
      }

      if (deviceType === 'bsj') {
        if (isUsingWsFlv) {
          const encoded = msgpack.encode([deviceCode, null])
          const encodedConfig = Array.from(encoded, (byte) =>
            byte.toString(16).padStart(2, '0')
          ).join('')

          return `${process.env.VUE_APP_TRACKVISION_WS_FLV_URL}/media/stream?r=${imei}/${channel}&c=${encodedConfig}`
        }

        return isHlsTesting
          ? `${process.env.VUE_APP_HLS_URL.replace(
              '{numFlag}',
              flag
            )}/live/stream/${imei}-${channel}.m3u8`
          : `${origin.replace('{numFlag}', flag)}/live/stream/${imei}-${channel}.flv`
      }
    },

    getVideoHistoryUrl(
      deviceCode,
      imei,
      channel,
      streamingEndpoint,
      startTime,
      endTime,
      isUsingWsFlv = false
    ) {
      const simplifyTime = (datetime) => {
        if (!datetime) return ''
        const parsedDatetime = moment.parseZone(datetime, moment.ISO_8601, true)
        return parsedDatetime.isValid() ? parsedDatetime.format('YYMMDDHHmmss') : ''
      }

      let origin = process.env.VUE_APP_RTMP_URL
      const deviceType = this.getDeviceType(deviceCode)
      if (streamingEndpoint === 'trackvision_indo') {
        origin = process.env.VUE_APP_RTMP_URL_SERVER_INDO
      }
      if (deviceType === 'jc') return `${origin.replace('{numFlag}', 1)}/live/${imei}.flv`
      if (deviceType === 'bsj') {
        if (isUsingWsFlv) {
          const encoded = msgpack.encode([
            deviceCode,
            0,
            [simplifyTime(startTime) + '-' + simplifyTime(endTime)],
            null,
          ])
          const encodedConfig = Array.from(encoded, (byte) =>
            byte.toString(16).padStart(2, '0')
          ).join('')

          return `${process.env.VUE_APP_TRACKVISION_WS_FLV_URL}/media/recording?r=${imei}/${channel}&c=${encodedConfig}`
        }
        return `${origin.replace('{numFlag}', 1)}/live/stream/${imei}-${channel}.flv`
      }
    },

    huggedText(text, maxLength, type = 'between') {
      if (!text || text.length <= maxLength) return text
      if (type == 'between') {
        const partLength = Math.floor((maxLength - 3) / 2)
        return text.slice(0, partLength) + '...' + text.slice(-partLength)
      } else if (type == 'last') {
        return text.slice(0, maxLength - 3) + '...'
      } else {
        return '...' + text.slice(0, -(maxLength - 3))
      }
    },

    getThumbnailVideoUrl(videoUrl) {
      const temp = videoUrl.split('?')[0]
      const arr = temp.split('/')
      const filename = arr[arr.length - 1]
      const arrFilename = filename.split('.')
      arrFilename[0] = 'THUMB_' + arrFilename[0]
      arrFilename[arrFilename.length - 1] = 'jpg'
      const thumbFilaname = arrFilename.join('.')
      arr[arr.length - 1] = thumbFilaname
      const restUrl = arr.slice(3).join('/')
      return process.env.VUE_APP_TRACKVISION_CDN_URL + '/' + restUrl
    },

    isDms(alertCategoryId) {
      return alertCategoryId === 3
    },

    isAdas(alertCategoryId) {
      return alertCategoryId === 4
    },

    isDmsAdas(alertCategoryId) {
      return this.isDms(alertCategoryId) || this.isAdas(alertCategoryId)
    },

    getChannelAlert(alertCategoryId, deviceCode) {
      const deviceType = this.getDeviceType(deviceCode)
      const isAdas = this.isAdas(alertCategoryId)
      const isDms = this.isDms(alertCategoryId)
      const alertType = isAdas ? 'ADAS' : isDms ? 'DMS' : null
      if (deviceType === 'jc') {
        return (
          TRACKVISION_CHANNEL_MAPPING.CONCOX[alertType][deviceCode] ??
          TRACKVISION_CHANNEL_MAPPING.CONCOX[alertType][TRACKVISION_CHANNEL_DEFAULT_KEY]
        )
      } else if (deviceType === 'bsj') {
        return (
          TRACKVISION_CHANNEL_MAPPING.BSJ[alertType][deviceCode] ??
          TRACKVISION_CHANNEL_MAPPING.BSJ[alertType][TRACKVISION_CHANNEL_DEFAULT_KEY]
        )
      } else if (deviceType === 'vss-howen') {
        return (
          TRACKVISION_CHANNEL_MAPPING.VSS_HOWEN[alertType][deviceCode] ??
          TRACKVISION_CHANNEL_MAPPING.VSS_HOWEN[alertType][TRACKVISION_CHANNEL_DEFAULT_KEY]
        )
      }
      return null
    },

    formatLabelTimezone(gmt) {
      if (gmt === 7) return 'WIB'
      else if (gmt === 8) return 'WITA'
      else if (gmt === 9) return 'WIT'
      else if (gmt > 0) return 'GMT+' + gmt
      return 'GMT-' + gmt
    },
    formatISODuration(value) {
      if (!value) return '-'
      const duration = moment.duration(value)
      const h = Math.floor(duration.asHours())
      const m = duration.minutes()

      let result = ''
      if (h > 0) result += `${h} ${this.$t('global.time.hour')} `
      if (m > 0) result += `${m} ${this.$t('global.time.minutes')}`

      return result.trim() || `0 ${this.$t('global.time.minutes')}`
    },
    isDeviceTrackvisionOffline(
      thresholdSecondEngineOn,
      thresholdSecondEngineOff,
      lastReceive,
      engineOn
    ) {
      const diffMinutes = this.getDurationBetween2Dates(lastReceive, this.nowUtc())
      const calculatedThreshold = engineOn ? thresholdSecondEngineOn : thresholdSecondEngineOff
      return diffMinutes > calculatedThreshold / 60
    },
    convertHHMMSSIntervalToIso8601(value) {
      try {
        const splittedValue = value.split(':')
        // eslint-disable-next-line no-undef
        const duration = Temporal.Duration.from({
          hours: splittedValue[0],
          minutes: splittedValue[1],
          seconds: splittedValue[2],
        })
        return duration.toString()
      } catch {
        return null
      }
    },
    convertIso8601ToHHMMSSInterval(value) {
      try {
        // eslint-disable-next-line no-undef
        const duration = Temporal.Duration.from(value)
        return duration.toLocaleString('en-US', {
          hours: '2-digit',
          minutes: '2-digit',
          seconds: '2-digit',
          style: 'digital',
        })
      } catch {
        return null
      }
    },

    async convertVideoAndGetFile(url) {
      const path = this.extractPathFromDomain(url)
      try {
        const res = await fetch(process.env.VUE_APP_API_CONVERT_VIDEO + '/' + path, {
          method: 'GET',
        })
        if (!res.ok) throw new Error('Failed to fetch file')
        const blob = await res.blob()
        const downloadUrl = window.URL.createObjectURL(blob)
        const contentDisposition = res.headers.get('Content-Disposition')
        let filename
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1]
          }
        }
        return { downloadUrl, filename }
      } catch (error) {
        this.$mcToast.error('Failed convert video')
        console.error(error)
        return null
      }
    },

    generatePictureUrlFromVideo(videoUrl, options = {}) {
      const {
        frame = 'start',
        time = null,
        outputType = 'image/png',
        quality = 0.85,
        timeoutMs = 10000,
        fetchAsBlob = false,
        crossOrigin = 'anonymous',
      } = options

      return new Promise(async (resolve) => {
        const video = document.createElement('video')
        const canvas = document.createElement('canvas')
        let isResolved = false
        let objectUrl = null
        let timeout = null

        const cleanup = () => {
          window.clearTimeout(timeout)
          video.removeAttribute('src')
          video.load()

          if (objectUrl) {
            URL.revokeObjectURL(objectUrl)
          }
        }

        const finish = (value) => {
          if (isResolved) return
          isResolved = true
          cleanup()
          resolve(value)
        }

        const captureFrame = () => {
          window.requestAnimationFrame(() => {
            try {
              if (!video.videoWidth || !video.videoHeight) {
                finish(null)
                return
              }

              canvas.width = video.videoWidth
              canvas.height = video.videoHeight

              canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)

              finish(canvas.toDataURL(outputType, quality))
            } catch (error) {
              console.error(error)
              finish(null)
            }
          })
        }

        const getTargetTime = () => {
          if (typeof time === 'number') return time
          if (frame === 'middle' && Number.isFinite(video.duration)) return video.duration / 2
          return 0
        }

        const seekAndCapture = () => {
          const targetTime = getTargetTime()

          if (targetTime <= 0) {
            video.addEventListener('loadeddata', captureFrame, { once: true })
            return
          }

          video.addEventListener('seeked', captureFrame, { once: true })
          video.currentTime = Math.min(targetTime, Math.max(video.duration - 0.1, 0))
        }

        timeout = window.setTimeout(() => finish(null), timeoutMs)

        try {
          let finalVideoUrl = videoUrl

          if (fetchAsBlob) {
            const res = await fetch(videoUrl, { mode: 'cors' })
            if (!res.ok) {
              finish(null)
              return
            }

            const blob = await res.blob()
            objectUrl = URL.createObjectURL(blob)
            finalVideoUrl = objectUrl
          }

          if (crossOrigin) {
            video.crossOrigin = crossOrigin
          }

          video.muted = true
          video.preload = 'auto'
          video.playsInline = true

          video.addEventListener('loadedmetadata', seekAndCapture, { once: true })
          video.addEventListener('error', () => finish(null), { once: true })

          video.src = finalVideoUrl
          video.load()
        } catch (error) {
          console.error(error)
          finish(null)
        }
      })
    },

    generatePictureUrlFromTsVideo(tsUrl, options = {}) {
      const {
        frame = 'start', // start | middle
        time = null,
        outputType = 'image/png',
        quality = 0.85,
        timeoutMs = 15000,
        mimeCodec = 'video/mp4; codecs="mp4a.40.2,avc1.64001f"',
      } = options

      const getBufferedTargetTime = (video, frame = 'start') => {
        if (!video.buffered || video.buffered.length === 0) return 0

        const start = video.buffered.start(0)
        const end = video.buffered.end(0)

        if (frame === 'middle') return start + (end - start) / 2
        return start + 0.05
      }

      return new Promise(async (resolve) => {
        const video = document.createElement('video')
        const canvas = document.createElement('canvas')
        const mediaSource = new MediaSource()
        const objectUrl = URL.createObjectURL(mediaSource)

        let isResolved = false
        let timeout = null

        const cleanup = () => {
          window.clearTimeout(timeout)
          video.removeAttribute('src')
          video.load()
          URL.revokeObjectURL(objectUrl)
        }

        const finish = (value) => {
          if (isResolved) return
          isResolved = true
          cleanup()
          resolve(value)
        }

        const captureFrame = () => {
          try {
            if (!video.videoWidth || !video.videoHeight) {
              finish(null)
              return
            }

            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)

            finish(canvas.toDataURL(outputType, quality))
          } catch (error) {
            console.error(error)
            finish(null)
          }
        }

        timeout = window.setTimeout(() => finish(null), timeoutMs)

        try {
          if (!window.MediaSource || !MediaSource.isTypeSupported(mimeCodec)) {
            finish(null)
            return
          }

          video.muted = true
          video.preload = 'auto'
          video.playsInline = true
          video.src = objectUrl

          mediaSource.addEventListener(
            'sourceopen',
            async () => {
              try {
                const response = await fetch(tsUrl, { mode: 'cors' })
                if (!response.ok) {
                  finish(null)
                  return
                }

                const buffer = await response.arrayBuffer()
                const transmuxer = new muxjs.mp4.Transmuxer()
                const sourceBuffer = mediaSource.addSourceBuffer(mimeCodec)

                transmuxer.on('data', (segment) => {
                  const data = new Uint8Array(
                    segment.initSegment.byteLength + segment.data.byteLength
                  )
                  data.set(segment.initSegment, 0)
                  data.set(segment.data, segment.initSegment.byteLength)

                  sourceBuffer.appendBuffer(data)

                  sourceBuffer.addEventListener(
                    'updateend',
                    async () => {
                      if (mediaSource.readyState === 'open') {
                        mediaSource.endOfStream()
                      }

                      const targetTime = getBufferedTargetTime(video, frame)

                      video.addEventListener(
                        'seeked',
                        () => {
                          const previewUrl = captureFrame()
                          resolve(previewUrl)
                        },
                        { once: true }
                      )

                      video.currentTime = targetTime
                    },
                    { once: true }
                  )

                  transmuxer.off('data')
                })

                transmuxer.on('error', (error) => {
                  console.error(error)
                  finish(null)
                })

                transmuxer.push(new Uint8Array(buffer))
                transmuxer.flush()
              } catch (error) {
                console.error(error)
                finish(null)
              }
            },
            { once: true }
          )
        } catch (error) {
          console.error(error)
          finish(null)
        }
      })
    },

    getDefaultUpdateFileListAlertDateRange(rawAlertCreatedOn) {
      const rawAlertMoment = moment.utc(rawAlertCreatedOn)
      const now = moment.utc()
      const startRange = rawAlertMoment.clone().subtract(6, 'hours')
      const endRangeCandidate = rawAlertMoment.clone().add(6, 'hours')
      const endRange = moment.min(endRangeCandidate, now)
      return { start: startRange, end: endRange }
    },

    /**
     * Sorts an array of vehicle group items, pushing any group named 'Default'
     * (case-insensitive) to the end of the list. Original name casing is preserved.
     *
     * @param {Object[]} vgroups - An array of vehicle group objects, each with a `vgroup.name` or `name` property.
     * @returns {Object[]} A new array of `{ name }` objects with 'Default' entries moved to the end.
     */
    getSortedVgroupItems(vgroups) {
      if (!vgroups || vgroups.length === 0) return []
      const items = vgroups.map((item) => ({
        name: item.vgroup?.name || item.name || '-',
      }))
      const normal = items.filter((item) => item.name.toUpperCase() !== 'DEFAULT')
      const defaults = items.filter((item) => item.name.toUpperCase() === 'DEFAULT')
      return [...normal, ...defaults]
    },
    /**
     *
     * @param {string} time - A time string in the format 'HH:mm' (e.g., '08:30').
     * @returns {number} Total minutes converted from the time string, returning 0 if the input is invalid.
     * @example
     * timeToMinutes('04:00') // 240
     * timeToMinutes('01:30') // 90
     */
    timeToMinutes(time) {
      if (!time) return 0
      const [hours, minutes] = String(time).split(':').map(Number)
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0
      return hours * 60 + minutes
    },
    /**
     *
     * @param {number} totalMinutes - Total minutes to convert back to a time string.
     * @returns {string} A time string in the format 'HH:mm' (e.g., '08:30').
     * @example
     * minutesToTime(240) // '04:00'
     * minutesToTime(90) // '01:30'
     */
    minutesToTime(totalMinutes) {
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    },
  },
}

export function debounce(fn, delay) {
  var timeoutID = null
  return function () {
    clearTimeout(timeoutID)
    var args = arguments
    var that = this
    timeoutID = setTimeout(function () {
      fn.apply(that, args)
    }, delay)
  }
}
